// Rail preference — whether the left nav rail rests EXPANDED or COLLAPSED,
// persisted per device (pre-sync).
//
// J35 (owner call 2026-09-30): the left rail should be EXPANDED by default, but
// a deliberate COLLAPSE must stick across reloads rather than re-expanding every
// load. Mirrors the right dock (dockPreference.ts, J19), the theme (theme.tsx)
// and the panel widths (panelLayout.ts): this is DEVICE display chrome, not a
// domain fact (memory project_theme_outside_event_log). It round-trips through
// its OWN localStorage key and is never emitted as a domain event. Only the
// impure shell reads/writes; the parse/serialize pair is pure and unit-tested.
//
// DEFAULT-EXPANDED lives HERE (the device-preference layer), not in the pure
// `initialFrame` fold: the fold accepts whatever seed it is handed, and this
// accessor decides that an unseen preference means "expanded." The pure frame
// stays policy-free; the persistence layer owns the default.
//
// Parse, don't validate: localStorage hands back `string | null` written by ANY
// earlier build. An absent key (fresh device) or a corrupt value degrades to the
// default (expanded); an explicit "collapsed" restores COLLAPSED.
//
// "hidden" is NOT a preference: it is a transient narrow-viewport fallback the
// responsive layer applies (AppShell), never a state the author chooses. So the
// persistable rail states are exactly EXPANDED | COLLAPSED; a "hidden" nav is
// left un-persisted so the last real preference survives (see `writeRailPreference`).

import type { NavRailState } from "../components/AppShell/AppShell.js";
import { readDeviceKey, writeDeviceKey } from "./deviceStore.js";

const STORAGE_KEY = "bilda.navExpanded";

// The rail state a fresh device rests in: EXPANDED (owner call 2026-09-29,
// reversing the 2026-08-20 collapsed default — see frame.ts). Mirrors the right
// dock's default-open posture (dockPreference.ts).
const DEFAULT_RAIL: RailPreference = "expanded";

// The persistable rail states — the subset of `NavRailState` an author can
// deliberately choose. "hidden" is a transient responsive fallback, never a
// preference, so it is excluded from the stored union (types over raw strings).
export type RailPreference = "expanded" | "collapsed";

// --- Parsing / serializing (pure) --------------------------------------------

const parseRailPreference = (raw: string | null): RailPreference =>
    raw === "collapsed" ? "collapsed" : DEFAULT_RAIL;

const serializeRailPreference = (pref: RailPreference): string => pref;

// Narrow a live nav factor to a persistable preference: a "hidden" (transient
// narrow-viewport) nav is NOT a preference and yields `null` — the caller leaves
// the last real preference untouched. The other two members ARE the preference.
const railPreferenceOf = (nav: NavRailState): RailPreference | null =>
    nav === "hidden" ? null : nav;

// Below this window width the menu starts collapsed whatever the stored
// preference (pen sidebar-collapse spec: "below about 1100 px the menu starts
// collapsed"). Only the START: resizing later never moves it.
const NARROW_START_PX = 1100;

// The rail a page load starts with: collapsed on a narrow window, else the
// device preference. Pure, so the policy is testable without a window.
export const startingRail = (
    pref: RailPreference,
    viewportWidth: number,
): RailPreference => (viewportWidth < NARROW_START_PX ? "collapsed" : pref);

// --- The impure seam (host-only, like dockPreference.ts / theme.tsx) ----------

const readRailPreference = (): RailPreference =>
    parseRailPreference(readDeviceKey(STORAGE_KEY));

// The starting rail for this window (host-only: reads the viewport width).
export const readStartingRail = (): RailPreference =>
    startingRail(readRailPreference(), window.innerWidth);

export const writeRailPreference = (nav: NavRailState): void => {
    const pref = railPreferenceOf(nav);
    if (pref !== null)
        writeDeviceKey(STORAGE_KEY, serializeRailPreference(pref));
};
